import { useState, useId, Children, isValidElement, cloneElement } from "react";
import { clsx } from "clsx";
import classes from "./ReadMoreContent.module.scss";
import { ReadMoreContentProps } from "./ReadMoreContentProps";

export const ReadMoreContent: React.FC<ReadMoreContentProps> = ({
  visibleContent,
  hiddenContent,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const id = useId();
  const hiddenContentId = `readmore-hidden-${id}`;

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const showMoreButton = (
    <button
      key="readmore-show-more"
      type="button"
      className={classes.toggleButton}
      onClick={toggleExpanded}
      aria-expanded={isExpanded}
      aria-controls={hiddenContentId}
      hidden={isExpanded}
    >
      Read more
    </button>
  );

  const showLessButton = (
    <button
      key="readmore-show-less"
      type="button"
      className={classes.toggleButton}
      onClick={toggleExpanded}
      aria-expanded={isExpanded}
      aria-controls={hiddenContentId}
    >
      Read less
    </button>
  );

  const injectButton = (
    content: React.ReactNode,
    button: React.ReactNode,
  ): React.ReactNode => {
    const childrenArray = Children.toArray(content);

    if (childrenArray.length === 0) {
      return button;
    }

    return childrenArray.map((child, index) => {
      const isLastChild = index === childrenArray.length - 1;

      if (!isLastChild) {
        return child;
      }

      if (isValidElement(child)) {
        const elementChild = child as React.ReactElement<{
          children?: React.ReactNode;
        }>;

        return cloneElement(elementChild, {
          ...elementChild.props,
          children: (
            <>
              {elementChild.props.children} {button}
            </>
          ),
        });
      }

      if (typeof child === "string" || typeof child === "number") {
        return (
          <>
            {child} {button}
          </>
        );
      }

      return child;
    });
  };

  return (
    <div className={className}>
      <div className={clsx(isExpanded && classes.lastChildFormatter)}>
        {injectButton(visibleContent, showMoreButton)}
      </div>

      <div
        id={hiddenContentId}
        className={classes.foldedContent}
        hidden={!isExpanded}
      >
        {injectButton(hiddenContent, showLessButton)}
      </div>
    </div>
  );
};

export default ReadMoreContent;
